import { selectionEmpty, transactionChanged, getPluginState } from '@remirror/core-utils';
import { PluginKey, Plugin } from 'prosemirror-state';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { noop, bool, isString, isRegExp, findMatches, isUndefined } from '@remirror/core-helpers';
import { DecorationSet, Decoration } from 'prosemirror-view';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { NULL_CHARACTER } from '@remirror/core-constants';
import { keydownHandler } from 'prosemirror-keymap';
import escapeStringRegex from 'escape-string-regexp';

var DEFAULT_SUGGEST_ACTIONS = {
  command: noop,
  create: noop,
  remove: noop,
  update: noop
};

var defaultHandler = function defaultHandler() {
  return false;
};

var DEFAULT_SUGGESTER = {
  appendText: '',
  createCommand: function createCommand() {
    return noop;
  },
  ignoredClassName: undefined,
  ignoredTag: 'span',
  invalidPrefixCharacters: undefined,
  keyBindings: {},
  matchOffset: 0,
  noDecorations: false,
  onChange: defaultHandler,
  onCharacterEntry: defaultHandler,
  onExit: defaultHandler,
  startOfLine: false,
  suggestClassName: 'suggest',
  suggestTag: 'span',
  supportedCharacters: /[\w\d_]+/,
  validPrefixCharacters: /^[\s\0]?$/
};
/**
 * The potential reasons for an exit of a mention.
 */

var ExitReason;
/**
 * The potential reason for changes
 */

(function (ExitReason) {
  ExitReason["End"] = "exit-end";
  ExitReason["Removed"] = "delete";
  ExitReason["Split"] = "exit-split";
  ExitReason["InvalidSplit"] = "invalid-exit-split";
  ExitReason["MoveEnd"] = "move-end";
  ExitReason["MoveStart"] = "move-start";
  ExitReason["JumpForward"] = "jump-forward-exit";
  ExitReason["JumpBackward"] = "jump-backward-exit";
  ExitReason["SelectionOutside"] = "selection-outside";
})(ExitReason || (ExitReason = {}));

var ChangeReason;

(function (ChangeReason) {
  ChangeReason["Start"] = "start";
  ChangeReason["Text"] = "change-character";
  ChangeReason["SelectionInside"] = "selection-inside";
  ChangeReason["Move"] = "move";
  ChangeReason["JumpBackward"] = "jump-backward-change";
  ChangeReason["JumpForward"] = "jump-forward-change";
})(ChangeReason || (ChangeReason = {}));

/**
 * Is this a change in the current suggestion (added or deleted characters)?
 */
var isChange = function isChange(compare) {
  return bool(compare.prev && compare.next && compare.prev.queryText.full !== compare.next.queryText.full);
};
/**
 * Has the cursor moved within the current suggestion (added or deleted
 * characters)?
 */

var isMove = function isMove(compare) {
  return bool(compare.prev && compare.next && compare.prev.range.to !== compare.next.range.to);
};
/**
 * Are we entering a new suggestion?
 */

var isEntry = function isEntry(compare) {
  return bool(!compare.prev && compare.next);
};
/**
 * Are we exiting a suggestion?
 */

var isExit = function isExit(compare) {
  return bool(compare.prev && !compare.next);
};
/**
 * Is this a jump from one suggestion to another?
 */

var isJump = function isJump(compare) {
  return bool(compare.prev && compare.next && compare.prev.range.from !== compare.next.range.from);
};
/**
 * Check that the passed in value is an ExitReason
 */

var isExitReason = function isExitReason(value) {
  return isString(value) && Object.values(ExitReason).includes(value);
};
var isChangeReason = function isChangeReason(value) {
  return isString(value) && Object.values(ChangeReason).includes(value);
};
/**
 * Checks that the reason passed is a split reason. This typically means that we
 * should default to a partial update / creation of the mention.
 */

var isSplitReason = function isSplitReason(value) {
  return value === ExitReason.Split;
};
/**
 * Checks that the reason was caused by a split at a point where there is no
 * query.
 */

var isInvalidSplitReason = function isInvalidSplitReason(value) {
  return value === ExitReason.InvalidSplit;
};
/**
 * Checks that the reason was caused by a deletion.
 */

var isRemovedReason = function isRemovedReason(value) {
  return value === ExitReason.Removed;
};
/**
 * Checks to see if this is a jump reason.
 */

var isJumpReason = function isJumpReason(map) {
  return map.exit ? [ExitReason.JumpBackward, ExitReason.JumpForward].includes(map.exit.reason) : map.change ? [ChangeReason.JumpBackward, ChangeReason.JumpForward].includes(map.change.reason) : false;
};
/**
 * True when the match is currently active (i.e. it's query has a value)
 */

var isValidMatch = function isValidMatch(match) {
  return bool(match && match.queryText.full.length >= match.suggester.matchOffset);
};
/**
 * True when the current selection is outside the match.
 */

var selectionOutsideMatch = function selectionOutsideMatch(_ref) {
  var match = _ref.match,
      selection = _ref.selection;
  return match && (selection.from < match.range.from || selection.from > match.range.end);
};

var escapeChar = function escapeChar(_char) {
  return escapeStringRegex(_char);
};
/**
 * Convert a RegExp into a string
 *
 * @param regexOrString
 */

var regexToString = function regexToString(regexOrString) {
  return isRegExp(regexOrString) ? regexOrString.source : regexOrString;
};
/**
 * Find regex prefix when depending on whether the mention only supports the
 * start of a line or not
 *
 * @param onlyStartOfLine
 */

var getRegexPrefix = function getRegexPrefix(onlyStartOfLine) {
  return onlyStartOfLine ? '^' : '';
};
/**
 * Get the supported characters regex string.
 */

var getRegexSupportedCharacters = function getRegexSupportedCharacters(supportedCharacters, matchOffset) {
  return "(?:".concat(regexToString(supportedCharacters), "){").concat(matchOffset, ",}");
};
/**
 * Create a regex expression to evaluate matches directly from the suggester properties.
 */


var createRegexFromSuggester = function createRegexFromSuggester(_ref) {
  var _char2 = _ref["char"],
      matchOffset = _ref.matchOffset,
      startOfLine = _ref.startOfLine,
      supportedCharacters = _ref.supportedCharacters;
  var flags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'gm';
  return new RegExp("".concat(getRegexPrefix(startOfLine)).concat(escapeChar(_char2)).concat(getRegexSupportedCharacters(supportedCharacters, matchOffset)), flags);
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Small utility method for creating a match with the reason property available.
 */
var createMatchWithReason = function createMatchWithReason(_ref) {
  var match = _ref.match,
      reason = _ref.reason;
  return _objectSpread({}, match, {
    reason: reason
  });
};
/**
 * Checks to see if the text before the matching character is a valid prefix.
 *
 * @param prefix - the prefix to test
 * @param params - an object with the regex testing values
 */


var isPrefixValid = function isPrefixValid(prefix, _ref2) {
  var invalidPrefixCharacters = _ref2.invalidPrefixCharacters,
      validPrefixCharacters = _ref2.validPrefixCharacters;

  if (!isUndefined(invalidPrefixCharacters)) {
    var regex = new RegExp(regexToString(invalidPrefixCharacters));
    return !regex.test(prefix);
  }

  {
    var _regex = new RegExp(regexToString(validPrefixCharacters));

    return _regex.test(prefix);
  }
};
/**
 * Find the position of a mention for a given selection and character
 *
 * @param params
 */


var findPosition = function findPosition(_ref3) {
  var text = _ref3.text,
      regexp = _ref3.regexp,
      $pos = _ref3.$pos,
      _char = _ref3["char"],
      suggester = _ref3.suggester;
  var position;
  var cursor = $pos.pos; // The current cursor position

  var start = $pos.start(); // The starting position for matches

  findMatches(text, regexp).forEach(function (match) {
    // Check the character before the current match to ensure it is not one of
    // the supported characters
    var matchPrefix = match.input.slice(Math.max(0, match.index - 1), match.index);

    if (isPrefixValid(matchPrefix, suggester)) {
      var from = match.index + start; // The absolute position of the match wrapper node

      var end = from + match[0].length; // The position where the match ends

      var to = Math.min(end, cursor); // The cursor position (or end position whichever is greater)

      var matchLength = to - from; // The length of the current match
      // If the $position is located within the matched substring, return that
      // range

      if (from < cursor && end >= cursor) {
        position = {
          range: {
            from: from,
            end: end,
            to: to
          },
          queryText: {
            partial: match[0].slice(_char.length, matchLength),
            full: match[0].slice(_char.length)
          },
          matchText: {
            partial: match[0].slice(0, matchLength),
            full: match[0]
          },
          suggester: suggester
        };
      }
    }
  });
  return position;
};
/**
 * Checks if any matches exist at the current selection for so that the
 * suggestions be activated or deactivated.
 */


var findMatch = function findMatch(_ref4) {
  var $pos = _ref4.$pos,
      suggester = _ref4.suggester;
  var _char2 = suggester["char"],
      name = suggester.name,
      startOfLine = suggester.startOfLine,
      supportedCharacters = suggester.supportedCharacters,
      matchOffset = suggester.matchOffset; // Create the regular expression to match the text against

  var regexp = createRegexFromSuggester({
    "char": _char2,
    matchOffset: matchOffset,
    startOfLine: startOfLine,
    supportedCharacters: supportedCharacters
  }); // All the text in the current node

  var text = $pos.doc.textBetween($pos.before(), $pos.end(), NULL_CHARACTER, NULL_CHARACTER); // Find the position and return it

  return findPosition({
    suggester: suggester,
    text: text,
    regexp: regexp,
    $pos: $pos,
    "char": _char2,
    name: name
  });
};
/**
 * Checks the provided match and generates a new match. This is useful for
 * determining the kind of change that has happened.
 *
 * If the match still exists and it is different then it's likely a split has
 * occurred.
 */


var recheckMatch = function recheckMatch(_ref5) {
  var state = _ref5.state,
      match = _ref5.match;

  try {
    // Wrapped in try catch because it's possible for everything to be deleted
    // and the doc.resolve will fail.
    return findMatch({
      $pos: state.doc.resolve(match.range.to),
      suggester: match.suggester
    });
  } catch (_unused) {
    return undefined;
  }
};
/**
 * Check whether the insert action occurred at the end, in the middle or caused
 * the suggestion to be invalid.
 *
 * Prev refers to the original previous and next refers to the updated version
 * after the split
 */


var createInsertReason = function createInsertReason(_ref6) {
  var prev = _ref6.prev,
      next = _ref6.next,
      state = _ref6.state;

  // Has the text been removed? TODO how to tests for deletions mid document?
  if (!next && prev.range.from >= state.doc.nodeSize) {
    return {
      exit: createMatchWithReason({
        match: prev,
        reason: ExitReason.Removed
      })
    };
  } // Are we within an invalid split?


  if (!next || !prev.queryText.partial) {
    return {
      exit: createMatchWithReason({
        match: prev,
        reason: ExitReason.InvalidSplit
      })
    };
  } // Are we at the end position?


  if (prev.range.end === next.range.to) {
    return {
      exit: createMatchWithReason({
        match: next,
        reason: ExitReason.End
      })
    };
  } // Are we in the middle of the mention


  if (prev.queryText.partial) {
    return {
      exit: createMatchWithReason({
        match: next,
        reason: ExitReason.Split
      })
    };
  }

  return {};
};
/**
 * Find the reason for the Jump
 */


var findJumpReason = function findJumpReason(_ref7) {
  var prev = _ref7.prev,
      next = _ref7.next,
      state = _ref7.state;
  var value = Object.create(null);
  var updatedPrev = recheckMatch({
    state: state,
    match: prev
  });

  var _ref8 = updatedPrev && updatedPrev.queryText.full !== prev.queryText.full // has query changed
  ? createInsertReason({
    prev: prev,
    next: updatedPrev,
    state: state
  }) : value,
      exit = _ref8.exit;

  var isJumpForward = prev.range.from < next.range.from;

  if (isJumpForward) {
    return {
      exit: exit !== null && exit !== void 0 ? exit : createMatchWithReason({
        match: prev,
        reason: ExitReason.JumpForward
      }),
      change: createMatchWithReason({
        match: next,
        reason: ChangeReason.JumpForward
      })
    };
  }

  return {
    exit: exit !== null && exit !== void 0 ? exit : createMatchWithReason({
      match: prev,
      reason: ExitReason.JumpBackward
    }),
    change: createMatchWithReason({
      match: next,
      reason: ChangeReason.JumpBackward
    })
  };
};
/**
 * Find the reason for the exit.
 *
 * This provides some context and helps sets up a helper command with sane
 * defaults.
 */


var findExitReason = function findExitReason(_ref9) {
  var match = _ref9.match,
      state = _ref9.state,
      $pos = _ref9.$pos;
  var selection = state.selection;
  var updatedPrev = recheckMatch({
    match: match,
    state: state
  }); // Exit created a split

  if (!updatedPrev || updatedPrev.queryText.full !== match.queryText.full) {
    return createInsertReason({
      prev: match,
      next: updatedPrev,
      state: state
    });
  } // Exit caused by a selection


  if (!selectionEmpty(state) && (selection.from <= match.range.from || selection.to >= match.range.end)) {
    return {
      exit: createMatchWithReason({
        match: match,
        reason: ExitReason.SelectionOutside
      })
    };
  } // Exit happened at the end of previous suggestion


  if ($pos.pos > match.range.end) {
    return {
      exit: createMatchWithReason({
        match: match,
        reason: ExitReason.MoveEnd
      })
    };
  } // Exit happened at the start of previous suggestion


  if ($pos.pos <= match.range.from) {
    return {
      exit: createMatchWithReason({
        match: match,
        reason: ExitReason.MoveStart
      })
    };
  }

  return {};
};

/**
 * Transforms the keybindings into an object that can be consumed by the
 * prosemirror keydownHandler method.
 */
var transformKeyBindings = function transformKeyBindings(_ref10) {
  var bindings = _ref10.bindings,
      params = _ref10.params;
  var keys = Object.create(null);
  return Object.entries(bindings).reduce(function (prev, _ref11) {
    var _ref12 = _slicedToArray(_ref11, 2),
        key = _ref12[0],
        method = _ref12[1];

    return _objectSpread({}, prev, _defineProperty({}, key, function () {
      return bool(method(params));
    }));
  }, keys);
};
/**
 * Run the keyBindings when a key is pressed to perform actions.
 *
 * When return value is `true` no further actions should be taken for this key
 * event. When `false` the event will be passed up the chain to the next key
 * handler.
 *
 * This is useful for intercepting events.
 */

var runKeyBindings = function runKeyBindings(bindings, params) {
  return keydownHandler(transformKeyBindings({
    bindings: bindings,
    params: params
  }))(params.view, params.event);
};

/**
 * Creates an array of the actions taken based on the current prev and next
 * state field
 */
var findReason = function findReason(_ref13) {
  var prev = _ref13.prev,
      next = _ref13.next,
      state = _ref13.state,
      $pos = _ref13.$pos;
  var value = Object.create(null);

  if (!prev && !next) {
    return value;
  }

  var compare = {
    prev: prev,
    next: next
  }; // Check for a Jump

  if (isJump(compare)) {
    return findJumpReason({
      prev: compare.prev,
      next: compare.next,
      state: state
    });
  } // Entered into a new suggestion


  if (isEntry(compare)) {
    return {
      change: createMatchWithReason({
        match: compare.next,
        reason: ChangeReason.Start
      })
    };
  } // Exited a suggestion


  if (isExit(compare)) {
    return findExitReason({
      $pos: $pos,
      match: compare.prev,
      state: state
    });
  }

  if (isChange(compare)) {
    return {
      change: createMatchWithReason({
        match: compare.next,
        reason: ChangeReason.Text
      })
    };
  }

  if (isMove(compare)) {
    return {
      change: createMatchWithReason({
        match: compare.next,
        reason: selectionEmpty(state) ? ChangeReason.Move : ChangeReason.SelectionInside
      })
    };
  }

  return value;
};
/**
 * Find a match for the provided matchers
 */

var findFromSuggesters = function findFromSuggesters(_ref14) {
  var suggesters = _ref14.suggesters,
      $pos = _ref14.$pos;
  // Find the first match and break when done
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = suggesters[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var suggester = _step.value;

      try {
        var match = findMatch({
          suggester: suggester,
          $pos: $pos
        });

        if (match) {
          return match;
        }
      } catch (_unused2) {
        console.warn('Error while finding match.');
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator["return"] != null) {
        _iterator["return"]();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return undefined;
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * The suggestion state which manages the list of suggestions.
 */

var SuggestState =
/*#__PURE__*/
function () {
  _createClass(SuggestState, [{
    key: "getCommand",

    /**
     * The actions created by the extension.
     */
    value: function getCommand(match, reason) {
      return match.suggester.createCommand({
        match: match,
        reason: reason,
        view: this.view,
        setMarkRemoved: this.setRemovedTrue,
        addIgnored: this.addIgnored,
        clearIgnored: this.clearIgnored
      });
    }
    /**
     * Returns the current active suggestion state field if one exists
     */

  }, {
    key: "match",
    get: function get() {
      return this.next ? this.next : this.prev && this.handlerMatches.exit ? this.prev : undefined;
    }
    /**
     * Create the state for the `prosemirror-suggest` plugin.
     *
     * @remarks
     *
     * Each suggester must provide a name value which is globally unique since it
     * acts as the identifier.
     *
     * It is possible to register multiple suggesters with identical `char`
     * properties. The matched suggester is based on the specificity of the
     * `regex` and the order in which they are passed in. Earlier suggesters are
     * prioritized.
     */

  }], [{
    key: "create",

    /**
     * Create an instance of the SuggestState class.
     */
    value: function create(suggesters) {
      return new SuggestState(suggesters);
    }
    /**
     * The suggesters that have been registered for the suggestions plugin.
     */

  }]);

  function SuggestState(suggesters) {
    var _this = this;

    _classCallCheck(this, SuggestState);

    _defineProperty(this, "handlerMatches", Object.create(null));

    _defineProperty(this, "ignored", DecorationSet.empty);

    _defineProperty(this, "removed", false);

    _defineProperty(this, "setRemovedTrue", function () {
      _this.removed = true;
    });

    _defineProperty(this, "onViewUpdate", function () {
      var match = _this.match,
          _this$handlerMatches = _this.handlerMatches,
          change = _this$handlerMatches.change,
          exit = _this$handlerMatches.exit; // Cancel update when a suggestion isn't active

      if (!change && !exit || !isValidMatch(match)) {
        return;
      } // Call Handlers
      // When a jump happens run the action that involves the position that occurs
      // later in the document. This is so that changes don't affect previous
      // positions.


      if (change && exit && isJumpReason({
        change: change,
        exit: exit
      })) {
        var exitParams = _this.createReasonParams(exit);

        var changeParams = _this.createReasonParams(change);

        var movedForwards = exit.range.from < change.range.from;
        movedForwards ? change.suggester.onChange(changeParams) : exit.suggester.onExit(exitParams);
        movedForwards ? exit.suggester.onExit(exitParams) : change.suggester.onChange(changeParams);
        _this.removed = false;
        return;
      }

      if (change) {
        change.suggester.onChange(_this.createReasonParams(change));
      }

      if (exit) {
        exit.suggester.onExit(_this.createReasonParams(exit));
        _this.removed = false;

        if (isInvalidSplitReason(exit.reason)) {
          _this.handlerMatches = Object.create(null);
        }
      }
    });

    _defineProperty(this, "addIgnored", function (_ref) {
      var from = _ref.from,
          _char = _ref["char"],
          name = _ref.name,
          _ref$specific = _ref.specific,
          specific = _ref$specific === void 0 ? false : _ref$specific;
      var to = from + _char.length;

      var suggester = _this.suggesters.find(function (val) {
        return val.name === name;
      });

      if (!suggester) {
        throw new Error("No suggester exists for the name provided: ".concat(name));
      }

      var attrs = suggester.ignoredClassName ? {
        "class": suggester.ignoredClassName
      } : {};
      var decoration = Decoration.inline(from, to, _objectSpread$1({
        nodeName: suggester.ignoredTag
      }, attrs), {
        "char": _char,
        name: name,
        specific: specific
      });
      _this.ignored = _this.ignored.add(_this.view.state.doc, [decoration]);
    });

    _defineProperty(this, "removeIgnored", function (_ref2) {
      var from = _ref2.from,
          _char2 = _ref2["char"],
          name = _ref2.name;

      var decorations = _this.ignored.find(from, from + _char2.length);

      var decoration = decorations[0];

      if (!bool(decoration) || decoration.spec.name !== name) {
        return;
      }

      _this.ignored = _this.ignored.remove([decoration]);
    });

    _defineProperty(this, "clearIgnored", function (name) {
      if (name) {
        var decorations = _this.ignored.find();

        var decorationsToClear = decorations.filter(function (_ref3) {
          var spec = _ref3.spec;
          return spec.name === name;
        });
        _this.ignored = _this.ignored.remove(decorationsToClear);
      } else {
        _this.ignored = DecorationSet.empty;
      }
    });

    var names = [];
    this.suggesters = suggesters.map(function (suggester) {
      if (names.includes(suggester.name)) {
        throw new Error("A suggester already exists with the name '".concat(suggester.name, "'. The name provided must be unique."));
      }

      names.push(suggester.name);
      return _objectSpread$1({}, DEFAULT_SUGGESTER, {}, suggester);
    });
  }
  /**
   * Initialize the SuggestState with a view which is stored for use later.
   */


  _createClass(SuggestState, [{
    key: "init",
    value: function init(view) {
      this.view = view;
      return this;
    }
    /**
     * Create the props which should be passed into each action handler
     */

  }, {
    key: "createParams",
    value: function createParams(match) {
      return _objectSpread$1({
        view: this.view,
        addIgnored: this.addIgnored,
        clearIgnored: this.clearIgnored,
        command: this.getCommand(match)
      }, match);
    }
    /**
     * Create the prop to be passed into the `onChange` or `onExit` handler.
     */

  }, {
    key: "createReasonParams",
    value: function createReasonParams(match) {
      return _objectSpread$1({}, this.createParams(match), {
        command: this.getCommand(match, match.reason)
      }, match);
    }
    /**
     * Manages the view updates.
     */

  }, {
    key: "mapIgnoredDecorations",

    /**
     * Update the current ignored decorations based on the latest changes to the
     * prosemirror document.
     */
    value: function mapIgnoredDecorations(tr) {
      // Map over and update the ignored decorations.
      var ignored = this.ignored.map(tr.mapping, tr.doc);
      var decorations = ignored.find(); // For suggesters with multiple characters it is possible for a `paste` or
      // any edit action within the decoration to expand the ignored section. We check for
      // that here and if the section size has changed it should be marked as
      // invalid and removed from the ignored `DecorationSet`.

      var invalid = decorations.filter(function (_ref4) {
        var from = _ref4.from,
            to = _ref4.to,
            spec = _ref4.spec;

        if (to - from !== spec["char"].length) {
          return true;
        }

        return false;
      });
      this.ignored = ignored.remove(invalid);
    }
    /**
     * Ignores the match specified. Until the match is deleted no more `onChange`,
     * `onExit` handlers will be triggered. It will be like the match doesn't
     * exist.
     *
     * @remarks
     *
     * All we need to ignore is the match character. This means that any further
     * matches from the activation character will be ignored.
     */

  }, {
    key: "shouldIgnoreMatch",
    value: function shouldIgnoreMatch(_ref5) {
      var range = _ref5.range,
          name = _ref5.suggester.name;
      var decorations = this.ignored.find();
      return decorations.some(function (_ref6) {
        var spec = _ref6.spec,
            from = _ref6.from;

        if (from !== range.from) {
          return false;
        }

        var shouldIgnore = spec.specific ? spec.name === name : true;
        return shouldIgnore;
      });
    }
    /**
     * Reset the state.
     */

  }, {
    key: "resetState",
    value: function resetState() {
      this.handlerMatches = Object.create(null);
      this.next = undefined;
      this.removed = false;
    }
    /**
     * Update the next state value.
     */

  }, {
    key: "updateReasons",
    value: function updateReasons(_ref7) {
      var $pos = _ref7.$pos,
          state = _ref7.state;
      var match = findFromSuggesters({
        suggesters: this.suggesters,
        $pos: $pos
      });
      this.next = match && this.shouldIgnoreMatch(match) ? undefined : match; // Store the matches with reasons

      this.handlerMatches = findReason({
        next: this.next,
        prev: this.prev,
        state: state,
        $pos: $pos
      });
    }
    /**
     * Used to handle the view property of the plugin spec.
     */

  }, {
    key: "viewHandler",
    value: function viewHandler() {
      return {
        update: this.onViewUpdate
      };
    }
  }, {
    key: "toJSON",
    value: function toJSON() {
      return this.match;
    }
    /**
     * Applies updates to the state to be used within the plugins apply method.
     *
     * @param - params
     */

  }, {
    key: "apply",
    value: function apply(_ref8) {
      var tr = _ref8.tr,
          newState = _ref8.newState;
      var exit = this.handlerMatches.exit;

      if (!transactionChanged(tr) && !this.removed) {
        return this;
      }

      this.mapIgnoredDecorations(tr); // If the previous run was an exit reset the suggestion matches

      if (exit) {
        this.resetState();
      }

      this.prev = this.next; // Match against the current selection position

      this.updateReasons({
        $pos: tr.selection.$from,
        state: newState
      });
      return this;
    }
    /**
     * Manages the keyDown event within the plugin props
     *
     * @param event
     */

  }, {
    key: "handleKeyDown",
    value: function handleKeyDown(event) {
      var match = this.match;

      if (!isValidMatch(match)) {
        return false;
      }

      var keyBindings = match.suggester.keyBindings;

      var params = _objectSpread$1({
        event: event,
        setMarkRemoved: this.setRemovedTrue
      }, this.createParams(match));

      return runKeyBindings(keyBindings, params);
    }
    /**
     * Handle any key presses of non supported characters
     */

  }, {
    key: "handleTextInput",
    value: function handleTextInput(_ref9) {
      var text = _ref9.text,
          from = _ref9.from,
          to = _ref9.to;
      var match = this.match;

      if (!isValidMatch(match)) {
        return false;
      }

      var onCharacterEntry = match.suggester.onCharacterEntry;
      return onCharacterEntry(_objectSpread$1({}, this.createParams(match), {
        from: from,
        to: to,
        text: text
      }));
    }
    /**
     * Handle the decorations which wrap the mention while it is active and not
     * yet complete.
     */

  }, {
    key: "decorations",
    value: function decorations(state) {
      var match = this.match;

      if (!isValidMatch(match)) {
        return this.ignored;
      }

      if (match.suggester.noDecorations) {
        return this.ignored;
      }

      var range = match.range,
          _match$suggester = match.suggester,
          name = _match$suggester.name,
          decorationsTag = _match$suggester.suggestTag,
          suggestionClassName = _match$suggester.suggestClassName;
      var from = range.from,
          end = range.end;
      return this.shouldIgnoreMatch(match) ? this.ignored : this.ignored.add(state.doc, [Decoration.inline(from, end, {
        nodeName: decorationsTag,
        "class": name ? "".concat(suggestionClassName, " ").concat(suggestionClassName, "-").concat(name) : suggestionClassName
      })]);
    }
  }]);

  return SuggestState;
}();

var suggestPluginKey = new PluginKey('suggest');
/**
 * Get the state of the suggest plugin.
 *
 * @param state - the editor state.
 */

var getSuggestPluginState = function getSuggestPluginState(state) {
  return getPluginState(suggestPluginKey, state);
};
/**
 * This creates a suggestion plugin with all the suggestions provided.
 *
 * @remarks
 *
 * In the following example we're creating an emoji suggestion plugin that
 * responds to the colon character with a query and presents a list of matching
 * emojis based on the query typed so far.
 *
 * ```ts
 * import { Suggester, suggest } from 'prosemirror-suggest';
 *
 * const maxResults = 10;
 * let selectedIndex = 0;
 * let emojiList: string[] = [];
 * let showSuggestions = false;
 *
 * const suggestEmojis: Suggester = {
 *   // By default decorations are used to highlight the currently matched
 *   // suggestion in the dom.
 *   // In this example we don't need decorations (in fact they cause problems when the
 *   // emoji string replaces the query text in the dom).
 *   noDecorations: true,
 *   char: ':', // The character to match against
 *   name: 'emoji-suggestion', // a unique name
 *   appendText: '', // Text to append to the created match
 *
 *   // Keybindings are similar to prosemirror keymaps with a few extra niceties.
 *   // The key identifier can also include modifiers (e.g.) `Cmd-Space: () => false`
 *   // Return true to prevent any further keyboard handlers from running.
 *   keyBindings: {
 *     ArrowUp: () => {
 *       selectedIndex = rotateSelectionBackwards(selectedIndex, emojiList.length);
 *     },
 *     ArrowDown: () => {
 *       selectedIndex = rotateSelectionForwards(selectedIndex, emojiList.length);
 *     },
 *     Enter: ({ command }) => {
 *       if (showSuggestions) {
 *         command(emojiList[selectedIndex]);
 *       }
 *     },
 *     Esc: () => {
 *       showSuggestions = false;
 *     },
 *   },
 *
 *   onChange: params => {
 *     const query = params.query.full;
 *     emojiList = sortEmojiMatches({ query, maxResults });
 *     selectedIndex = 0;
 *     showSuggestions = true;
 *   },
 *
 *   onExit: () => {
 *     showSuggestions = false;
 *     emojiList = [];
 *     selectedIndex = 0;
 *   },
 *
 *   // Create a  function that is passed into the change, exit and keybinding handlers.
 *   // This is useful when these handlers are called in a different part of the app.
 *   createCommand: ({ match, view }) => {
 *     return (emoji,skinVariation) => {
 *       if (!emoji) {
 *         throw new Error('An emoji is required when calling the emoji suggestions command');
 *       }
 *
 *       const tr = view.state.tr; const { from, end: to } = match.range;
 *       tr.insertText(emoji, from, to); view.dispatch(tr);
 *     };
 *   },
 * };
 *
 *  // Create the plugin with the above configuration. It also supports multiple plugins being added.
 * const suggestionPlugin = suggest(suggestEmojis);
 *
 *  // Include the plugin in the created editor state.
 * const state = EditorState.create({schema,
 *   plugins: [suggestionPlugin],
 * });
 * ```
 *
 * The priority of the suggestions is the order in which they are passed into
 * this function.
 *
 * - `const plugin = suggest(two, one, three)` - Here `two` will be checked
 *   first, then `one` and then `three`.
 *
 * Only one suggestion can match at any given time. The order and specificity of
 * the regex parameters help determines which suggestion will be active.
 *
 * @param suggesters - a list of suggesters in the order they should be
 * evaluated.
 */

var suggest = function suggest() {
  for (var _len = arguments.length, suggesters = new Array(_len), _key = 0; _key < _len; _key++) {
    suggesters[_key] = arguments[_key];
  }

  var pluginState = SuggestState.create(suggesters);
  return new Plugin({
    key: suggestPluginKey,
    // Handle the plugin view
    view: function view(_view) {
      return pluginState.init(_view).viewHandler();
    },
    state: {
      // Initialize the state
      init: function init() {
        return pluginState;
      },
      // Apply changes to the state
      apply: function apply(tr, _, oldState, newState) {
        return pluginState.apply({
          tr: tr,
          oldState: oldState,
          newState: newState
        });
      }
    },
    props: {
      // Call the keydown hook if suggestion is active.
      handleKeyDown: function handleKeyDown(_, event) {
        return pluginState.handleKeyDown(event);
      },
      // Defer to the pluginState handler
      handleTextInput: function handleTextInput(_, from, to, text) {
        return pluginState.handleTextInput({
          text: text,
          from: from,
          to: to
        });
      },
      // Sets up a decoration (styling options) on the currently active
      // decoration
      decorations: function decorations(state) {
        return pluginState.decorations(state);
      }
    }
  });
};

export { ChangeReason, DEFAULT_SUGGESTER, DEFAULT_SUGGEST_ACTIONS, ExitReason, createRegexFromSuggester, escapeChar, getRegexPrefix, getSuggestPluginState, isChange, isChangeReason, isEntry, isExit, isExitReason, isInvalidSplitReason, isJump, isJumpReason, isMove, isRemovedReason, isSplitReason, isValidMatch, regexToString, selectionOutsideMatch, suggest };
//# sourceMappingURL=prosemirror-suggest.esm.js.map
